import ApplicationController from './application_controller';
import Logger from '../lib/logger';
// A coordinate as a number, or NaN when it is missing: Number(null) and Number('') give 0, which is a real place.
const coordinate = (value) => value === null || value === undefined || value === '' ? NaN : Number(value);
// A map of points: a circle for each point that has coordinates, in the point's colour or the map's, showing its
// popup text when clicked. The map fits the circles, or shows `center` at `zoom` when there is none. Views give the
// points and the tiles through MapHelper#leaflet_markers_data. It draws the service type, service group, country and
// quote bundle maps (docs/MAP_CONTROLLERS.md).
class default_1 extends ApplicationController {
    constructor() {
        super(...arguments);
        this.map = null;
        this.resizeObserver = null;
    }
    connect() {
        super.connect();
        const leaflet = window.L;
        if (!leaflet) {
            Logger.error('Leaflet is not loaded; the map cannot be drawn', null, { controller: 'leaflet-markers' });
            return;
        }
        try {
            this.draw(leaflet);
        }
        catch (error) {
            this.handleError(error, { action: 'draw the map' });
        }
    }
    cleanup() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.map?.remove();
        this.map = null;
    }
    draw(leaflet) {
        const element = this.element;
        // A page Turbo restores from its cache still holds the old map's panes; Leaflet would draw over them.
        element.replaceChildren();
        const map = leaflet.map(element);
        this.map = map;
        leaflet.tileLayer(this.tileUrlValue, { attribution: this.tileAttributionValue, maxZoom: 19 }).addTo(map);
        const circles = this.markersValue.flatMap((point) => this.circle(leaflet, point));
        if (circles.length > 0) {
            const group = leaflet.featureGroup(circles);
            group.addTo(map);
            map.fitBounds(group.getBounds(), { padding: [24, 24], maxZoom: this.maxZoomValue });
        }
        else {
            map.setView(this.centerValue, this.zoomValue);
        }
        // A map drawn while hidden (in a tab, a modal) has no size yet; redraw it once it has one.
        this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
        this.resizeObserver.observe(element);
    }
    // A circle for a point that has coordinates, none for one that has not. The popup is text, never HTML: the
    // names come from imported data.
    circle(leaflet, point) {
        const lat = coordinate(point.lat);
        const lng = coordinate(point.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng))
            return [];
        const color = point.color || this.colorValue;
        const circle = leaflet.circleMarker([lat, lng], { radius: 7, weight: 1, color, fillColor: color, fillOpacity: 0.85 });
        if (point.popup) {
            const text = document.createElement('span');
            text.textContent = point.popup;
            circle.bindPopup(text);
        }
        return [circle];
    }
}
default_1.values = {
    markers: { type: Array, default: [] },
    tileUrl: { type: String, default: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
    tileAttribution: {
        type: String,
        default: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
    },
    center: { type: Array, default: [-15.7801, -47.9292] },
    zoom: { type: Number, default: 4 },
    maxZoom: { type: Number, default: 10 },
    color: { type: String, default: '#0d6efd' }
};
export default default_1;
